/**
 * Lift2Fit — the internal-coach invitation page (`#/crm/join-team?code=…`).
 *
 * This is where the link the gym sends actually lands. It existed as a constant
 * (`INTERNAL_COACH_JOIN_PATH`) with no page behind it, so an invitation was a
 * dead link for anyone who was not already inside the CRM.
 *
 * It is readable SIGNED OUT, because the whole point is that a coach may not
 * have an account yet:
 *   * the public peek names the gym, the email the invitation is bound to, and
 *     the gym's own nickname for the invitee — no other tenant data;
 *   * signed out, it offers sign-in and sign-up, both returning here (`?next=`);
 *   * signed in with the WRONG account, it says so and offers to sign out —
 *     never a bare 403 from the API;
 *   * signed in with the right account, one button joins.
 *
 * The email is the binding: the account that accepts must own the invited email.
 * That is enforced by the API regardless of what this page renders.
 */
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui';
import { AlertIcon, ArrowRightIcon, CheckIcon } from '@/lib/icons';
import { EmptyState, LoadingPanel, Panel, ScreenHeader, StatusChip } from '../components';
import { CRM_EYEBROW } from './shared';
import { useSession } from '@/lib/useSession';
import { Link, navigate } from '../router';

interface Preview {
  email: string;
  label: string | null;
  status: string;
  expired: boolean;
  gymName: string;
}

/**
 * The invitation code, captured at MODULE LOAD.
 *
 * The router adopts an initial `#/route?query` deep link and immediately
 * normalises the URL to a clean path, dropping the query — so by the time this
 * screen mounts (which can be a render later, once the session is known) the
 * code would be gone. Reading it at import time is the only point that is
 * reliably before the rewrite.
 */
const CODE_KEY = 'l2f.joinTeamCode';

const INITIAL_QUERY = (() => {
  if (typeof window === 'undefined') return '';
  const hash = window.location.hash;
  if (hash.includes('?')) return hash.split('?')[1] ?? '';
  return window.location.search.replace(/^\?/, '');
})();

const INITIAL_CODE = (() => {
  const fromUrl = new URLSearchParams(INITIAL_QUERY).get('code');
  if (typeof window === 'undefined') return null;
  if (fromUrl && fromUrl.trim() !== '') {
    // Remember it for the sign-in / sign-up round trip: those are separate
    // routes and the query does not survive the navigation.
    try {
      window.sessionStorage.setItem(CODE_KEY, fromUrl.trim());
    } catch {
      /* private mode — the URL case still works on this load */
    }
    return fromUrl.trim();
  }
  try {
    const stored = window.sessionStorage.getItem(CODE_KEY);
    return stored && stored.trim() !== '' ? stored.trim() : null;
  } catch {
    return null;
  }
})();

/** True when this load came from an invitation that has not been answered yet. */
export function pendingInvitationCode(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.sessionStorage.getItem(CODE_KEY);
    return stored && stored.trim() !== '' ? stored.trim() : null;
  } catch {
    return null;
  }
}

function clearPendingInvitation(): void {
  try {
    window.sessionStorage.removeItem(CODE_KEY);
  } catch {
    /* nothing to clear */
  }
  if (typeof window !== 'undefined') {
    // The router will normalise the URL on the next render; drop the code it
    // carries so a refresh does not reopen a used invitation.
    const clean = window.location.pathname + window.location.search.replace(/[?&]code=[^&]*/g, '');
    try {
      window.history.replaceState({}, '', clean);
    } catch {
      /* ignore */
    }
  }
}

/** The path to come back to after signing in — this page, with its code. */
function returnTo(code: string): string {
  return `/crm/join-team?code=${encodeURIComponent(code)}`;
}

export function CrmJoinTeamScreen() {
  const session = useSession();
  const code = INITIAL_CODE;
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<null | 'joined' | 'declined'>(null);

  useEffect(() => {
    if (!code) {
      setLoading(false);
      setError('This link is missing its invitation code.');
      return;
    }
    let cancelled = false;
    void api.internalCoachInvitePreview(code).then((result) => {
      if (cancelled) return;
      setLoading(false);
      if (result.problem || !result.data) {
        setError(
          result.problem?.detail ??
            'That invitation is not valid. It may have been withdrawn, or the link may be incomplete.',
        );
        return;
      }
      setPreview(result.data);
    });
    return () => {
      cancelled = true;
    };
  }, [code]);

  const signedInEmail = session.user?.email?.trim().toLowerCase() ?? null;
  const invitedEmail = preview?.email?.trim().toLowerCase() ?? null;
  const matches = signedInEmail !== null && invitedEmail !== null && signedInEmail === invitedEmail;

  const accept = async () => {
    if (!code) return;
    setBusy(true);
    const result = await api.acceptInternalCoachInvite(code);
    setBusy(false);
    if (result.problem) {
      setError(result.problem.detail ?? 'That invitation could not be accepted.');
      return;
    }
    clearPendingInvitation();
    setDone('joined');
  };

  const decline = async () => {
    if (!code) return;
    setBusy(true);
    const result = await api.declineInternalCoachInvite(code);
    setBusy(false);
    if (result.problem) {
      setError(result.problem.detail ?? 'That invitation could not be declined.');
      return;
    }
    clearPendingInvitation();
    setDone('declined');
  };

  const joinPath = code ? returnTo(code) : '/crm';
  const signInLink = `/crm/login?next=${encodeURIComponent(joinPath)}`;
  const signUpLink = `/crm/signup?next=${encodeURIComponent(joinPath)}`;

  return (
    <div className="l2f-app-root mx-auto flex min-h-screen w-full max-w-[720px] flex-col justify-center gap-6 px-4 py-12">
      <ScreenHeader
        eyebrow={CRM_EYEBROW}
        title={done === 'joined' ? 'You are on the team' : 'Join a gym’s team'}
        lead={
          done === 'joined'
            ? 'Your gym can now assign clients to you. You will find them on your Clients page — and nothing else of the gym’s is visible to you.'
            : 'A gym has invited you to coach its clients on Lift2Fit. You keep your own account: joining adds a team, it does not replace your profile.'
        }
      />

      {loading ? (
        <LoadingPanel label="Reading the invitation" rows={2} />
      ) : done !== null ? (
        <Panel testId="crm-join-team-done" title={done === 'joined' ? 'Joined' : 'Declined'}>
          <p className="text-[14px] text-ink-muted">
            {done === 'joined'
              ? 'Your gym can assign clients to you from its team page. Their clients, programmes and chat appear under Clients.'
              : 'The gym has been told you declined. Nothing was linked and nothing was shared.'}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              data-testid="crm-join-team-continue"
              onClick={() => navigate(done === 'joined' ? '/crm/clients' : '/crm')}
            >
              <CheckIcon /> {done === 'joined' ? 'Go to my clients' : 'Go to my workspace'}
            </Button>
          </div>
        </Panel>
      ) : error ? (
        <EmptyState
          title="We could not use this invitation"
          body={error}
          icon={<AlertIcon className="h-8 w-8 text-accent" />}
        />
      ) : preview ? (
        <Panel testId="crm-join-team" title={`${preview.gymName} invited you`} bodyClassName="gap-3">
          <ul className="flex flex-col gap-1 text-[14px] text-ink">
            <li>
              <span className="font-bold">Invited as:</span>{' '}
              {preview.label ?? (
                <span className="text-ink-muted">no nickname given</span>
              )}
            </li>
            <li>
              <span className="font-bold">Account email:</span> {preview.email}
            </li>
          </ul>

          {preview.status !== 'pending' || preview.expired ? (
            <p className="border-l-2 border-l-accent bg-page-subtle px-3 py-2 text-[13px] text-ink" data-testid="crm-join-team-unusable">
              This invitation is {preview.expired ? 'expired' : preview.status}. Ask the gym to send a new
              one.
            </p>
          ) : !session.isAuthenticated ? (
            <>
              <p className="text-[14px] text-ink-muted">
                Sign in, or create an account, with <span className="font-bold text-ink">{preview.email}</span>{' '}
                — that is the address the invitation is for, and the only one that can accept it.
              </p>
              <div className="flex flex-wrap gap-2">
                <Link
                  to={signInLink}
                  data-testid="crm-join-team-signin"
                  className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
                >
                  Sign in <ArrowRightIcon />
                </Link>
                <Link
                  to={signUpLink}
                  data-testid="crm-join-team-signup"
                  className="inline-flex h-11 items-center gap-2 border border-accent px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-accent hover:bg-accent hover:text-on-ink-inverse"
                >
                  Create an account <ArrowRightIcon />
                </Link>
              </div>
            </>
          ) : !matches ? (
            <>
              <p className="border-l-2 border-l-accent bg-page-subtle px-3 py-2 text-[13px] text-ink" data-testid="crm-join-team-mismatch">
                You are signed in as <span className="font-bold">{signedInEmail}</span>, but this invitation
                is for <span className="font-bold">{preview.email}</span>. Sign out and sign in with that
                address to accept it.
              </p>
              <Button
                variant="secondary"
                size="sm"
                data-testid="crm-join-team-switch"
                onClick={() => {
                  session.logout();
                  navigate(signInLink);
                }}
              >
                Sign out and switch
              </Button>
            </>
          ) : (
            <>
              <p className="text-[14px] text-ink-muted">
                You are signed in as {preview.email}. Joining adds {preview.gymName}’s team to this account:
                you will see the clients they assign, and nothing of theirs else — no payments, no other
                clients.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={busy}
                  data-testid="crm-join-team-accept"
                  onClick={() => void accept()}
                >
                  <CheckIcon /> Join {preview.gymName}’s team
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  data-testid="crm-join-team-decline"
                  onClick={() => void decline()}
                >
                  Decline
                </Button>
              </div>
            </>
          )}
        </Panel>
      ) : null}
    </div>
  );
}
